import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Lock, ArrowLeft, User, Loader2 } from "lucide-react";
import { getAuthErrorMessage } from "@/utils/authErrors";

const Login = () => {
  const [mode, setMode] = useState("social"); // "social" | "email-login" | "email-register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [oauthProcessing, setOauthProcessing] = useState(false);
  const { login, register, persistAndHydrateSession } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const isNativeApp = typeof window !== "undefined" && window.isNativeApp;

  // ── Referral code capture ──
  // Read ?ref=CODE off the URL when a friend lands here from a share link
  // and persist to localStorage so the code survives OAuth roundtrips
  // (Google/Apple bounce us out and back). We consume + clear the stored
  // code the moment we successfully create an account.
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref && /^[A-Za-z0-9_-]{3,64}$/.test(ref)) {
      try {
        localStorage.setItem("pending_ref_code", ref);
      } catch (_) { /* privacy mode etc — non-fatal */ }
    }
  }, [searchParams]);

  const getPendingRef = () => {
    try {
      return localStorage.getItem("pending_ref_code") || null;
    } catch (_) { return null; }
  };
  const clearPendingRef = () => {
    try {
      localStorage.removeItem("pending_ref_code");
    } catch (_) { /* no-op */ }
  };

  // ── DuoLyst Google OAuth callback handler ──
  // Backend redirects here with #google_token=xxx after successful Google auth
  useEffect(() => {
    const hash = window.location.hash;

    // Handle new DuoLyst-owned Google OAuth token
    if (hash?.includes("google_token=")) {
      const token = hash.split("google_token=")[1]?.split("&")[0];
      if (token) {
        window.history.replaceState(null, "", window.location.pathname);
        setOauthProcessing(true);

        // Exchange token for user data via /auth/me
        const hydrateUser = async () => {
          try {
            const res = await axios.get(`${API}/auth/me`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            await persistAndHydrateSession(token, res.data);
            // Ref was passed through OAuth state param and consumed by the
            // backend on account creation; clear the frontend copy so a
            // later signup doesn't accidentally re-attribute.
            clearPendingRef();
            toast.success(`Welcome, ${res.data.name || "Explorer"}!`);
            navigate("/dashboard", { replace: true });
          } catch (err) {
            console.error("[DuoLyst] Google token hydration failed:", err);
            toast.error("Google sign-in failed. Please try again.");
            setOauthProcessing(false);
          }
        };
        hydrateUser();
      }
    }

    // Handle legacy Emergent session_id (backward compat during migration)
    if (hash?.includes("session_id=")) {
      const sessionId = hash.split("session_id=")[1]?.split("&")[0];
      if (sessionId) {
        window.history.replaceState(null, "", window.location.pathname);
        setOauthProcessing(true);
        axios
          .post(`${API}/auth/oauth/session`, { session_id: sessionId })
          .then(async (res) => {
            const { token, user } = res.data;
            await persistAndHydrateSession(token, user);
            toast.success(`Welcome, ${user.name || "Explorer"}!`);
            navigate("/dashboard", { replace: true });
          })
          .catch((err) => {
            toast.error(getAuthErrorMessage(err, "Google sign-in failed"));
            setOauthProcessing(false);
          });
      }
    }

    // Handle error from Google callback
    const error = searchParams.get("error");
    if (error) {
      const errorMessages = {
        google_cancelled: "Google sign-in was cancelled",
        google_token_failed: "Google sign-in failed. Please try again.",
        google_profile_failed: "Could not retrieve Google profile",
        google_network: "Network error during Google sign-in",
        no_email: "Google account has no email address",
      };
      toast.error(errorMessages[error] || "Authentication failed");
    }
  }, [persistAndHydrateSession, navigate, searchParams]);

  // ── Apple Sign-In native bridge listener ──
  useEffect(() => {
    const handleAppleAuth = async (event) => {
      const msg = event.detail;
      // Clear timeout
      if (window._appleAuthTimeout) { clearTimeout(window._appleAuthTimeout); window._appleAuthTimeout = null; }
      if (msg.type === "APPLE_AUTH_SUCCESS") {
        setAppleLoading(true);
        try {
          const res = await axios.post(`${API}/auth/apple`, {
            identity_token: msg.identityToken,
            user: msg.user,
            email: msg.email,
            full_name: msg.fullName,
            referral_code: getPendingRef(),
          });
          const { token, user } = res.data;
          await persistAndHydrateSession(token, user);
          clearPendingRef();
          toast.success(`Welcome, ${user.name || "Explorer"}!`);
          navigate("/dashboard", { replace: true });
        } catch (err) {
          toast.error(getAuthErrorMessage(err, "Apple sign-in failed"));
          setAppleLoading(false);
        }
      } else if (msg.type === "APPLE_AUTH_CANCELLED") {
        setAppleLoading(false);
      } else if (msg.type === "APPLE_AUTH_ERROR") {
        setAppleLoading(false);
        toast.error(msg.error || "Apple sign-in failed");
      }
    };
    window.addEventListener("duolyst:apple-auth", handleAppleAuth);
    return () => window.removeEventListener("duolyst:apple-auth", handleAppleAuth);
  }, [persistAndHydrateSession, navigate]);

  // ── Google Sign-In handler ──
  const handleGoogleSignIn = () => {
    setOauthProcessing(true);
    // Include referral code in the OAuth URL so the backend can attribute
    // signups that came in via a share link, even though Google bounces
    // us through its own flow. Backend encodes this into the OAuth state
    // and extracts on callback.
    const ref = getPendingRef();
    const refQs = ref ? `&ref=${encodeURIComponent(ref)}` : "";
    const googleLoginUrl = isNativeApp
      ? `https://duolyst.co.uk/api/auth/google/login?mobile=1${refQs}`
      : `${API}/auth/google/login${ref ? `?ref=${encodeURIComponent(ref)}` : ""}`;

    if (isNativeApp && window.ReactNativeWebView) {
      // Open Google OAuth in SFSafariViewController — avoids WebView cookie issues
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ type: "OPEN_SAFARI_CHECKOUT", url: googleLoginUrl })
      );
      // Timeout fallback — if user doesn't return in 60s, show recovery
      setTimeout(() => setOauthProcessing(false), 60000);
    } else {
      window.location.href = googleLoginUrl;
    }
  };

  const handleAppleSignIn = () => {
    if (isNativeApp && window.signInWithApple) {
      setAppleLoading(true);
      // Reset loading after 15s timeout in case native callback never fires
      const timeout = setTimeout(() => {
        setAppleLoading(false);
        toast.error("Apple Sign-In timed out. Please try again.");
      }, 15000);
      window._appleAuthTimeout = timeout;
      window.signInWithApple();
    } else {
      toast.info("Apple Sign-In is available in the DuoLyst iOS app");
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Please fill in all fields"); return; }
    setLoading(true);
    try {
      if (mode === "email-register") {
        if (!name) { toast.error("Please enter your name"); setLoading(false); return; }
        await register(name, email, password, getPendingRef());
        clearPendingRef();
        toast.success("Account created!");
      } else {
        await login(email, password);
        toast.success("Welcome back!");
      }
      navigate("/dashboard", { replace: true });
    } catch (error) {
      toast.error(getAuthErrorMessage(error, "Authentication failed"));
    } finally {
      setLoading(false);
    }
  };

  // ── OAuth processing state ──
  if (oauthProcessing) {
    return (
      <div className="flex flex-col items-center justify-center bg-[#0B0F1A]" style={{ minHeight: '100dvh' }}>
        <img src="/logo.png" alt="DuoLyst" className="w-16 h-16 mb-6 object-contain" />
        <Loader2 className="w-7 h-7 text-[#E07A5F] animate-spin mb-3" />
        <p className="text-white/50 text-sm">Signing you in...</p>
        <button
          onClick={() => { setOauthProcessing(false); window.history.replaceState(null, "", "/login"); }}
          className="mt-8 text-white/30 text-xs hover:text-white/50 underline"
          data-testid="oauth-cancel-btn"
        >
          Cancel and return to login
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-[#0B0F1A] min-h-screen" style={{ minHeight: '-webkit-fill-available' }}>
      {mode !== "social" ? (
        <div className="px-6 pt-6">
          <button onClick={() => setMode("social")}
            className="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors text-sm"
            data-testid="back-to-social">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        </div>
      ) : (
        <div className="px-6 pt-6">
          <Link to="/"
            className="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors text-sm"
            data-testid="back-to-landing">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
        </div>
      )}

      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm mx-auto">
          <div className="text-center mb-10" style={{ animation: "fadeInUp 0.5s ease" }}>
            <img src="/logo.png" alt="DuoLyst" className="w-20 h-20 mx-auto mb-4 object-contain" />
            <h1 className="text-white text-2xl font-serif font-semibold tracking-tight mb-2">DuoLyst</h1>
            <p className="text-white/40 text-sm">Plan better trips together</p>
          </div>

          {mode === "social" && (
            <div className="space-y-3" style={{ animation: "fadeInUp 0.6s ease" }}>
              {isNativeApp && (
                <button onClick={handleAppleSignIn} disabled={appleLoading}
                  className="w-full h-[52px] rounded-xl bg-white text-[#0B0F1A] font-medium text-[15px] flex items-center justify-center gap-3 transition-all active:scale-[0.98] disabled:opacity-60"
                  style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif" }}
                  data-testid="apple-signin-btn">
                  {appleLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M13.64 9.12c-.02-2.12 1.73-3.14 1.81-3.19-1-1.44-2.5-1.64-3.04-1.66-1.29-.13-2.52.76-3.18.76-.66 0-1.67-.74-2.75-.72-1.41.02-2.72.82-3.45 2.09-1.47 2.56-.38 6.35 1.06 8.42.7 1.01 1.54 2.15 2.64 2.11 1.06-.04 1.46-.68 2.74-.68 1.28 0 1.64.68 2.76.66 1.14-.02 1.86-1.03 2.55-2.05.8-1.18 1.13-2.32 1.15-2.38-.03-.01-2.21-.85-2.29-3.36zM11.54 3.12c.58-.7.97-1.68.87-2.65-.84.03-1.86.56-2.46 1.27-.54.62-1.01 1.62-.88 2.57.93.07 1.89-.47 2.47-1.19z" fill="currentColor"/>
                    </svg>
                  )}
                  {appleLoading ? "Signing in..." : "Continue with Apple"}
                </button>
              )}

              <button onClick={() => setMode("email-login")}
                className="w-full h-[52px] rounded-xl bg-white/[0.08] border border-white/[0.12] text-white font-medium text-[15px] flex items-center justify-center gap-3 transition-all hover:bg-white/[0.12] active:scale-[0.98]"
                data-testid="email-signin-btn">
                <Mail className="w-[18px] h-[18px]" /> Continue with Email
              </button>

              <p className="text-center text-white/25 text-[11px] mt-6 leading-relaxed">
                By continuing, you agree to DuoLyst's{" "}
                <Link to="/terms" className="underline hover:text-white/40">Terms</Link>{" "}and{" "}
                <Link to="/privacy" className="underline hover:text-white/40">Privacy Policy</Link>
              </p>
            </div>
          )}

          {(mode === "email-login" || mode === "email-register") && (
            <form onSubmit={handleEmailSubmit} className="space-y-4" style={{ animation: "fadeInUp 0.4s ease" }}
              autoComplete="on">
              <h2 className="text-white text-xl font-serif font-semibold mb-1">
                {mode === "email-register" ? "Create Account" : "Sign In"}
              </h2>
              <p className="text-white/40 text-sm mb-6">
                {mode === "email-register" ? "Start your journey" : "Welcome back, Explorer"}
              </p>

              {mode === "email-register" && (
                <div className="space-y-1.5">
                  <Label htmlFor="name" className="text-white/60 text-sm">Name</Label>
                  <div className="relative">
                    <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                    <Input id="name" name="name" type="text" autoComplete="name" placeholder="Your name"
                      value={name} onChange={(e) => setName(e.target.value)}
                      className="pl-10 h-12 bg-white/[0.05] border-white/[0.1] text-white placeholder:text-white/25 rounded-xl focus:border-[#E07A5F] focus:ring-[#E07A5F]"
                      data-testid="register-name-input" />
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-white/60 text-sm">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <Input id="email" name="email" type="email" autoComplete="email" placeholder="you@example.com"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12 bg-white/[0.05] border-white/[0.1] text-white placeholder:text-white/25 rounded-xl focus:border-[#E07A5F] focus:ring-[#E07A5F]"
                    data-testid="login-email-input" />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-white/60 text-sm">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <Input id="password" name="password" type="password"
                    autoComplete={mode === "email-register" ? "new-password" : "current-password"}
                    placeholder={mode === "email-register" ? "Create a password" : "Your password"}
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 h-12 bg-white/[0.05] border-white/[0.1] text-white placeholder:text-white/25 rounded-xl focus:border-[#E07A5F] focus:ring-[#E07A5F]"
                    data-testid="login-password-input" />
                </div>
              </div>

              {mode === "email-login" && (
                <div className="text-right">
                  <Link to="/forgot-password" className="text-xs text-white/40 hover:text-[#E07A5F] transition-colors"
                    data-testid="forgot-password-link">Forgot password?</Link>
                </div>
              )}

              <Button type="submit" disabled={loading}
                className="w-full h-12 bg-[#E07A5F] hover:bg-[#E07A5F]/90 text-white rounded-xl text-[15px] font-medium transition-all active:scale-[0.98]"
                data-testid="login-submit-btn">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : mode === "email-register" ? "Create Account" : "Sign In"}
              </Button>

              <p className="text-center text-white/40 text-sm mt-4">
                {mode === "email-login" ? (
                  <>Don't have an account?{" "}<button type="button" onClick={() => setMode("email-register")}
                    className="text-[#E07A5F] font-medium hover:underline" data-testid="go-to-register-link">Create one</button></>
                ) : (
                  <>Already have an account?{" "}<button type="button" onClick={() => setMode("email-login")}
                    className="text-[#E07A5F] font-medium hover:underline">Sign in</button></>
                )}
              </p>
            </form>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default Login;
